import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { IntegrationPageActionsProps } from '../IntegrationPage';
import OrderIntegrationButton from '../OrderIntegrationButton';
import DeactivateIntegrationButton from '../DeactivateIntegrationButton';
import { useOrderableModuleFeatures } from '../../billing/functions/useOrderableModuleFeatures';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useGlobalContext } from 'GlobalContext';
import { saveData } from 'services/api/saveData';

// The page actions of the mkg integration, it is activated directly without a form
const MkgIntegrationPageActions: React.FC<IntegrationPageActionsProps> = ({
    response,
    integrationSlug,
    featureKey,
    handleRefetch
}) => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const { environmentType } = useAuthContext();
    const { setFloatingAlert } = useGlobalContext();
    const [buttonLoader, setButtonLoader] = useState<boolean>(false);

    // Determine whether the paid integration feature is turned on, a deactivated feature shows the order button
    const moduleFeatures = useOrderableModuleFeatures();
    const isFeatureActive = featureKey ? environmentType === 'trial' || !!moduleFeatures?.activeFeatures.includes(featureKey) : true;

    // Activate the integration directly, there are no settings to fill in
    const activateIntegration = async () => {
        try {
            // Start the button loader
            setButtonLoader(true);

            // Create or reactivate the mkg integration
            const activationResponse = await saveData({
                apiUrl: `post_integration/${integrationSlug}`,
                method: 'post',
                data: {}
            });

            // Stop the button loader
            setButtonLoader(false);

            // Open the activated integration so its details are fetched
            if (activationResponse?.status === 201 && activationResponse.data?.id) {
                history.replace(`${location.pathname}?integration=${integrationSlug}&id=${activationResponse.data.id}`);
                handleRefetch?.();
            }
        } catch (error) {
            console.error('Error activating the mkg integration', error);

            // Stop the button loader
            setButtonLoader(false);

            // Show an alert that the integration could not be activated
            setFloatingAlert({ type: 'danger', message: t('integrations.mkg.activation_failed_error') });
        }
    };

    // Wait for the module features before showing the actions
    if (featureKey && environmentType !== 'trial' && !moduleFeatures) {
        return null;
    }

    return (
        <div className="integration-actions">
            {featureKey && !isFeatureActive ? (
                <OrderIntegrationButton
                    featureKey={featureKey}
                    onOrdered={handleRefetch}
                />
            ) : !response?.is_active ? (
                <PrimaryButton
                    label="integrations.general.activate_integration_label"
                    loading={buttonLoader}
                    onClick={activateIntegration}
                />
            ) : (
                <>
                    <PrimaryButton
                        label="integrations.mkg.go_to_job_import_label"
                        onClick={() => history.push('/import-data/job/')}
                    />
                    {featureKey && environmentType !== 'trial' && (
                        <DeactivateIntegrationButton
                            featureKey={featureKey}
                            onDeactivated={handleRefetch}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default MkgIntegrationPageActions;
